import Head from 'next/head';
import { ProductWithPrice } from 'types';

interface StructuredDataProps {
  products?: ProductWithPrice[];
  page?: 'home' | 'product' | 'other';
  productName?: string;
  productDescription?: string;
  productImage?: string[];
  includeFAQ?: boolean;
}

export default function StructuredData({
  products = [],
  page = 'home',
  productName,
  productDescription,
  productImage = [],
  includeFAQ = false,
}: StructuredDataProps) {
  // Organization data
  const organizationData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    url: 'https://easy-peasy.ai',
    logo: 'https://easy-peasy.ai/favicon.ico',
    name: 'Easy-Peasy.AI',
    description:
      'Easy-Peasy.AI is an All-In-One AI Platform that helps you create amazing content with AI tools for writing, images, audio, transcription, and more.',
    sameAs: [
      'https://twitter.com/easy_peasy_ai',
      'https://www.linkedin.com/company/easy-peasy-ai',
      'https://www.facebook.com/profile.php?id=100087681065513',
      'https://www.youtube.com/@easy-peasy-ai',
      'https://www.instagram.com/easy_peasy.ai/',
    ],
  };

  // Website data (without reviews - WebSite doesn't support reviews)
  const websiteData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Easy-Peasy.AI',
    url: 'https://easy-peasy.ai',
  };

  // Software Application with reviews - this is what supports reviews
  const softwareApplicationData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: productName || 'Easy-Peasy.AI All-In-One AI Platform',
    description:
      productDescription ||
      'All-in-one AI platform with 200+ AI tools for content creation, writing, images, audio, transcription, chatbots, and more',
    image:
      productImage.length > 0
        ? productImage
        : [
            'https://easy-peasy.ai/api/og-template?title=Easy-Peasy.AI%20All-In-One%20AI%20Platform',
          ],
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web Browser',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '10',
      bestRating: '5',
      worstRating: '1',
    },
    review: [
      {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '4.9',
          bestRating: '5',
        },
        author: {
          '@type': 'Organization',
          name: 'Capterra',
        },
      },
    ],
    ...(products && products.length > 0
      ? { offers: generateOffers(products) }
      : {}),
  };

  function generateOffers(products: ProductWithPrice[]) {
    const defaultOffers: any[] = [];

    if (products.length === 0) {
      return defaultOffers;
    }

    return products.map((product) => {
      const price = product.prices?.[0];
      const priceValue =
        price && price.unit_amount ? (price.unit_amount / 100).toString() : '0';

      return {
        '@type': 'Offer',
        price: priceValue,
        priceCurrency: price?.currency?.toUpperCase() || 'USD',
        description: `${product.name || 'AI Plan'} - ${product.description || 'AI platform features'}`,
      };
    });
  }

  // FAQ data for home page
  const faqData = includeFAQ
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Can I change my plan at any time?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, you can upgrade or downgrade your plan at any time.',
            },
          },
          {
            '@type': 'Question',
            name: 'Is there a discount for annual subscriptions?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, we offer a 40% discount if you choose the annual plan.',
            },
          },
          {
            '@type': 'Question',
            name: 'When will I be billed?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'You will be billed at the start of your subscription.',
            },
          },
          {
            '@type': 'Question',
            name: 'Is there a money-back guarantee?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: "If you don't find Easy-Peasy.AI meets your needs, we guarantee a full refund of your payment within 30 days of purchase—no questions asked.",
            },
          },
          {
            '@type': 'Question',
            name: 'Are there any hidden fees?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'No, there are no hidden fees. You just pay the monthly subscription fee.',
            },
          },
          {
            '@type': 'Question',
            name: 'How do I cancel my subscription?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'You can easily cancel your subscription at any time by going to your account settings.',
            },
          },
          {
            '@type': 'Question',
            name: 'Do you offer any kind of support?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, we offer email and live chat support for all of our customers.',
            },
          },
          {
            '@type': 'Question',
            name: 'What payment methods are accepted?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'We support all major credit cards and debit cards. Your payment method is secured by Stripe for your privacy and security. For PayPal payments, please contact us.',
            },
          },
        ],
      }
    : null;

  // Create the full structured data graph
  const structuredDataGraph: any[] = [
    organizationData,
    websiteData,
    softwareApplicationData,
  ];

  if (faqData) {
    structuredDataGraph.push(faqData);
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': structuredDataGraph,
  };

  // Breadcrumb data for specific pages
  const breadcrumbData =
    page !== 'home'
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://easy-peasy.ai',
            },
          ],
        }
      : null;

  return (
    <>
      <script
        id='structured-data'
        type='application/ld+json'
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData),
        }}
      />
      {breadcrumbData && (
        <script
          id='breadcrumbs'
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbData),
          }}
        />
      )}
    </>
  );
}
